
import { defineComponent, PropType, computed, Ref } from 'vue'
import { MESSAGE_TYPE_CONFIRM, addMessageQueue, MESSAGE_TYPE_ERROR } from '@/plugins/messageQueue'
import { ElForm } from 'element-plus'
import { ApplicantCreateRequest } from '@/requests/boundary'
import { BoundarySteps, RequestReasonTypes } from '@/constants/enums'
import JrButton from '@/components/jr-parts/JrButton.vue'
import JrFileInput from '@/components/jr-parts/JrFileInput.vue'
import Message from '@/services/message'
import { BoundaryForm } from '@/types/forms'
import { AxiosResponse } from 'axios'
import { Boundary } from '@/api'
import { startLoading, endLoading } from '@/plugins/loading'
import { STEP_CHANGE_LOADING_TIME } from '@/constants/const'

/**
 * POST用に入力内容を整理する
 * cloneしたobjectを返す
 */
const organizeBoundary = (inputs: BoundaryForm): Boundary => {
  const result: Boundary = {
    ...inputs,
  }
  // 申請理由その他
  if (result.request_reason_type !== RequestReasonTypes.OTHER.code) {
    delete result.request_reason_type_other
  }
  // ファイル系で必須ではないものの場合、-1ならnullにする
  // 委任状
  if (result.power_of_attorney_file === -1) {
    result.power_of_attorney_file = null
  }
  // 本人確認写(免許書等)
  if (result.identification_file === -1) {
    result.identification_file = null
  }
  // 地積測量図
  if (result.survey_drawing_of_acreage_file === -1) {
    result.survey_drawing_of_acreage_file = null
  }
  // 現地写真
  if (result.actual_place_photo_file === -1) {
    result.actual_place_photo_file = null
  }
  return result
}

/**
 * 境界立会申請のStepを次に進めるコンポーネント
 * 送信時はデータを送信する
 */
export default defineComponent({
  name: 'BoundaryNext',
  emits: ['update:modelValue', 'update:boundary', 'validationError'],
  components: {
    JrButton,
  },
  props: {
    boundary: {
      type: Object as PropType<BoundaryForm>,
      required: true,
    },
    // step
    modelValue: {
      type: Number,
      required: true,
    },
    formRef: {
      type: Object as PropType<InstanceType<typeof ElForm>>,
      required: false,
      default: null,
    },
    // ファイルアップロードコンポーネントref
    fileRefs: {
      type: Array as PropType<Array<Ref<InstanceType<typeof JrFileInput>>>>,
      required: true,
    },
  },
  setup(props, context) {
    const next = async () => {
      if (!props.formRef) {
        // DOM未生成時は何もしない
        return
      }

      if (props.modelValue === BoundarySteps.INPUT) {
        // 入力画面の場合、一瞬のローディング画面を挟む
        const loadingKey = startLoading()
        setTimeout(() => {
          endLoading(loadingKey)
        }, STEP_CHANGE_LOADING_TIME)
      }
      // バリデーションチェック
      props.formRef.validate((valid, fieldErrorObject) => {
        let errorMessage = ''
        if ((props.boundary.start_station == null) !== (props.boundary.end_station == null)) {
          // 開始駅・終了駅のどちらかのみ入力されている場合エラー
          if (props.boundary.start_station == null) {
            context.emit('validationError', {
              start_station: Message.bind(Message.codes.V_REQUIRED_SELECT_IF, {
                target: '終了駅',
                attribute: '開始駅',
              }),
            })
            errorMessage +=
              Message.bind(Message.codes.V_REQUIRED_SELECT_IF, { target: '終了駅', attribute: '開始駅' }) + '\n'
          } else {
            context.emit('validationError', {
              end_station: Message.bind(Message.codes.V_REQUIRED_SELECT_IF, {
                target: '開始駅',
                attribute: '終了駅',
              }),
            })
            errorMessage +=
              Message.bind(Message.codes.V_REQUIRED_SELECT_IF, { target: '開始駅', attribute: '終了駅' }) + '\n'
          }
          valid = false
        } else {
          errorMessage = ''
          context.emit('validationError', {})
        }

        if (!valid) {
          // チェックNG
          //バリデーションのエラー内容をダイアログに表示
          for (let field in fieldErrorObject) {
            errorMessage += fieldErrorObject[field][0].message + '\n'
          }
          addMessageQueue(MESSAGE_TYPE_ERROR, errorMessage)
          return false
        }
        // 入力画面の場合、確認Stepにする
        if (props.modelValue === BoundarySteps.INPUT) {
          context.emit('update:modelValue', BoundarySteps.CONFIRM)
          return true
        }
        // 確認画面の場合、送信確認画面を開く
        if (props.modelValue === BoundarySteps.CONFIRM) {
          addMessageQueue(MESSAGE_TYPE_CONFIRM, Message.codes.CONFIRM_SEND, async () => {
            // OKなら送信する
            const loadingKey = 'boundary create'
            startLoading(loadingKey)
            const cancelLoadingCallback = () => {
              endLoading(loadingKey)
            }
            // ファイルアップロード
            let fileResult = true
            for (const ref of props.fileRefs) {
              const tmpFileResult = await ref.value.uploadFile(cancelLoadingCallback)
              if (!tmpFileResult) {
                fileResult = false
              }
            }

            // 全てのファイル送信が終われば登録
            if (fileResult) {
              const boundary = organizeBoundary(props.boundary)
              const request = new ApplicantCreateRequest(boundary)
              request.setErrorCallback(cancelLoadingCallback)
              await request.exec()
              if (request.success) {
                endLoading(loadingKey)
                // 登録成功したら完了Stepにする
                context.emit('update:modelValue', BoundarySteps.COMPLETE)
                // 登録した内容をemitする
                const { data } = request.getResponse() as AxiosResponse<Boundary>
                context.emit('update:boundary', data)
              }
            }
          })

          return true
        }
      })
    }
    const btnLabel = computed(() => {
      return props.modelValue === BoundarySteps.CONFIRM ? '送信' : '次へ'
    })
    return {
      btnLabel,
      next,
    }
  },
})
